import { useState } from "react";
import { Bell, Check, Pin, Plus, X } from "lucide-react";
import { usePageState } from "#/features/inventory/hooks";
import { useAuth } from "#/context/AuthProvider";
import {
  useApproveNotice,
  useCreateNotice,
  useDeleteNotice,
  useNoticesList,
  useRejectNotice,
  useUpdateNotice,
} from "../hooks";
import type { Notice, NoticeApprovalStatus, NoticeCategory } from "../types";

type FormState = {
  title: string;
  body: string;
  category: NoticeCategory;
  location: string;
  startLocal: string;
  endLocal: string;
  pinned: boolean;
  active: boolean;
};

const EMPTY_FORM: FormState = {
  title: "",
  body: "",
  category: "GENERAL",
  location: "",
  startLocal: "",
  endLocal: "",
  pinned: false,
  active: true,
};

type FieldErrors = { title?: boolean; body?: boolean; startLocal?: boolean; endLocal?: boolean };

const CATEGORY_LABEL: Record<NoticeCategory, string> = {
  MEETING: "Meeting",
  ACTIVITY: "Activity",
  GENERAL: "General",
};

const CATEGORY_CLS: Record<NoticeCategory, string> = {
  MEETING: "bg-blue-100 text-blue-700",
  ACTIVITY: "bg-purple-100 text-purple-700",
  GENERAL: "bg-slate-100 text-slate-600",
};

const APPROVAL_LABEL: Record<NoticeApprovalStatus, string> = {
  PENDING_APPROVAL: "Pending approval",
  APPROVED: "Approved",
  REJECTED: "Rejected",
};

const APPROVAL_CLS: Record<NoticeApprovalStatus, string> = {
  PENDING_APPROVAL: "bg-amber-100 text-amber-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700",
};

const STATUS_TABS: { label: string; value: NoticeApprovalStatus | "ALL" }[] = [
  { label: "All", value: "ALL" },
  { label: "Pending approval", value: "PENDING_APPROVAL" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
];

/** <input type="datetime-local"> gives "YYYY-MM-DDTHH:mm" in the browser's local time —
 *  treat it as local and let the Date constructor convert it to a UTC ISO string for the API. */
function localToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** Inverse of localToIso, for pre-filling the form when editing. */
function isoToLocal(iso?: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** "Now", in the same "YYYY-MM-DDTHH:mm" shape -- used as the start field's calendar
 *  floor so the picker itself refuses a past start, instead of only catching it on Save. */
function nowLocal(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmt(iso?: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fieldCls(hasError?: boolean) {
  return hasError ? "border-red-400 focus:border-red-400 focus:ring-red-300" : "";
}

function labelCls(hasError?: boolean) {
  return hasError ? "text-red-600" : "text-slate-700";
}

export default function NoticeAdminPage() {
  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const isApprover = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]) || hasPermission("HR_NOTICE_APPROVE");

  const { page, size, setPage, setSize } = usePageState(10);
  const [statusFilter, setStatusFilter] = useState<NoticeApprovalStatus | "ALL">("ALL");
  const { data, isLoading: loading, isError, error } = useNoticesList(page, size, statusFilter === "ALL" ? undefined : statusFilter);

  const [editing, setEditing] = useState<Notice | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const createMutation = useCreateNotice();
  const updateMutation = useUpdateNotice();
  const removeMutation = useDeleteNotice();
  const approveMutation = useApproveNotice();
  const rejectMutation = useRejectNotice();
  const saving = createMutation.isPending || updateMutation.isPending;

  function handleTabChange(value: NoticeApprovalStatus | "ALL") {
    setStatusFilter(value);
    setPage(0);
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFieldErrors({});
    setShowForm(true);
  }

  function openEdit(notice: Notice) {
    setEditing(notice);
    setForm({
      title: notice.title,
      body: notice.body,
      category: notice.category,
      location: notice.location ?? "",
      startLocal: isoToLocal(notice.startAt),
      endLocal: isoToLocal(notice.endAt),
      pinned: notice.pinned,
      active: notice.active,
    });
    setFormError(null);
    setFieldErrors({});
    setShowForm(true);
  }

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    if (key in fieldErrors) setFieldErrors((fe) => ({ ...fe, [key]: false }));
  }

  /** The end field's calendar floor is the chosen start, so changing the start to something
   *  at or after the current end would otherwise leave a stale, now-invalid end date sitting
   *  in the form until Save catches it -- clear it instead, same way the picker itself would
   *  refuse to show it as an option. */
  function handleStartChange(value: string) {
    setForm((f) => {
      const next = { ...f, startLocal: value };
      if (value && f.endLocal && new Date(f.endLocal).getTime() <= new Date(value).getTime()) {
        next.endLocal = "";
      }
      return next;
    });
    setFieldErrors((fe) => ({ ...fe, startLocal: false, endLocal: false }));
  }

  async function handleSave() {
    const missing: FieldErrors = {};
    if (!form.title.trim()) missing.title = true;
    if (!form.body.trim()) missing.body = true;
    if (!form.startLocal) missing.startLocal = true;
    if (!form.endLocal) missing.endLocal = true;

    if (Object.keys(missing).length > 0) {
      setFieldErrors(missing);
      return setFormError("Title, notice text, start and end date/time are all required.");
    }

    const startAt = localToIso(form.startLocal);
    const endAt = localToIso(form.endLocal);

    // Issuing a notice "for the past" makes no sense -- only enforced on create; an existing
    // notice can still be edited after its start time has already passed.
    if (!editing && startAt && new Date(startAt).getTime() < Date.now() - 60_000) {
      setFieldErrors({ startLocal: true });
      return setFormError("Start date/time cannot be in the past.");
    }

    if (startAt && endAt && new Date(endAt) <= new Date(startAt)) {
      setFieldErrors({ endLocal: true });
      return setFormError("End date/time must be after the start.");
    }

    setFieldErrors({});
    setFormError(null);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        category: form.category,
        location: form.location.trim() || null,
        startAt,
        endAt,
        pinned: form.pinned,
        active: form.active,
      };
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setShowForm(false);
      setEditing(null);
    } catch (e: any) {
      setFormError(e?.response?.data?.message ?? e?.message ?? "Save failed.");
    }
  }

  async function handleDelete(notice: Notice) {
    if (!window.confirm(`Delete "${notice.title}"? This can't be undone.`)) return;
    await removeMutation.mutateAsync(notice.id);
  }

  async function handleApprove(notice: Notice) {
    await approveMutation.mutateAsync({ id: notice.id });
  }

  async function handleReject(notice: Notice) {
    const remarks = window.prompt(`Reason for rejecting "${notice.title}" (optional):`) ?? undefined;
    await rejectMutation.mutateAsync({ id: notice.id, remarks });
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bell className="h-6 w-6 text-slate-400" />
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Notice Board</h1>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
        >
          <Plus className="h-4 w-4" />
          New Notice
        </button>
      </div>

      {showForm && (
        <div className="rounded-2xl border bg-white p-5 shadow-sm">
          <h3 className="mb-4 font-bold text-slate-900">{editing ? "Edit Notice" : "New Notice"}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            <label className={`space-y-1.5 text-sm font-medium md:col-span-2 ${labelCls(fieldErrors.title)}`}>
              Title <span className="text-red-500">*</span>
              <input
                className={`input ${fieldCls(fieldErrors.title)}`}
                value={form.title}
                onChange={(e) => updateField("title", e.target.value)}
                placeholder="All-staff meeting — Q3 review"
              />
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Category
              <select className="input" value={form.category} onChange={(e) => updateField("category", e.target.value as NoticeCategory)}>
                <option value="MEETING">Meeting</option>
                <option value="ACTIVITY">Activity</option>
                <option value="GENERAL">General</option>
              </select>
            </label>
            <label className="space-y-1.5 text-sm font-medium text-slate-700">
              Location
              <input className="input" value={form.location} onChange={(e) => updateField("location", e.target.value)} placeholder="Boardroom / Zoom link" />
            </label>
            <label className={`space-y-1.5 text-sm font-medium ${labelCls(fieldErrors.startLocal)}`}>
              Starts <span className="text-red-500">*</span>
              <input
                type="datetime-local"
                className={`input ${fieldCls(fieldErrors.startLocal)}`}
                value={form.startLocal}
                min={editing ? undefined : nowLocal()}
                onChange={(e) => handleStartChange(e.target.value)}
              />
            </label>
            <label className={`space-y-1.5 text-sm font-medium ${labelCls(fieldErrors.endLocal)}`}>
              Ends <span className="text-red-500">*</span>
              <input
                type="datetime-local"
                className={`input ${fieldCls(fieldErrors.endLocal)}`}
                value={form.endLocal}
                min={form.startLocal || undefined}
                disabled={!form.startLocal}
                title={!form.startLocal ? "Set the start date/time first" : undefined}
                onChange={(e) => updateField("endLocal", e.target.value)}
              />
              {!form.startLocal && <span className="block text-[11px] font-normal text-slate-400">Set the start date/time first</span>}
            </label>
            <div className="md:col-span-2">
              <label className={`space-y-1.5 text-sm font-medium ${labelCls(fieldErrors.body)}`}>
                Notice text <span className="text-red-500">*</span>
                <textarea
                  className={`input min-h-24 ${fieldCls(fieldErrors.body)}`}
                  value={form.body}
                  onChange={(e) => updateField("body", e.target.value)}
                  placeholder="Details of the meeting or activity..."
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={form.pinned} onChange={(e) => updateField("pinned", e.target.checked)} />
              Pin to top of the board
            </label>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input type="checkbox" checked={form.active} onChange={(e) => updateField("active", e.target.checked)} />
              Active (visible on the dashboard)
            </label>
          </div>
          {!isApprover && (
            <p className="mt-3 rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-700">
              You don't hold notice-approval rights, so this notice will wait for an administrator
              (or a "Notice Admin") to approve it before it appears on the dashboard.
            </p>
          )}
          {formError && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">{formError}</p>}
          <div className="mt-4 flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-xl border px-4 py-2 text-sm font-semibold">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Small report: every notice published, by whom, and its approval state. */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => handleTabChange(t.value)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
              statusFilter === t.value ? "bg-emerald-700 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="rounded-xl border bg-white p-5 text-sm text-slate-500">Loading notices…</div>}
      {isError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <strong>Error:</strong> {(error as any)?.response?.data?.message ?? (error as any)?.message ?? "Failed to load notices"}
        </div>
      )}

      {!loading && !isError && (
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Starts</th>
                <th className="px-4 py-3">Ends</th>
                <th className="px-4 py-3">Published by</th>
                <th className="px-4 py-3">Approval</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.content ?? []).map((notice) => (
                <tr key={notice.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    <span className="flex items-center gap-1.5">
                      {notice.pinned && <Pin className="h-3.5 w-3.5 shrink-0 text-amber-500" />}
                      {notice.title}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${CATEGORY_CLS[notice.category]}`}>
                      {CATEGORY_LABEL[notice.category]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmt(notice.startAt)}</td>
                  <td className="px-4 py-3 text-slate-500">{fmt(notice.endAt)}</td>
                  <td className="px-4 py-3 text-slate-600">{notice.createdBy}</td>
                  <td className="px-4 py-3">
                    {/* Just the status -- "Published by" already shows who's responsible for
                        this notice, so a second "by {approvedBy}" line here was redundant. */}
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${APPROVAL_CLS[notice.approvalStatus]}`}>
                      {APPROVAL_LABEL[notice.approvalStatus]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${notice.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      {notice.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {isApprover && notice.approvalStatus === "PENDING_APPROVAL" && (
                      <>
                        <button onClick={() => handleApprove(notice)} title="Approve" className="mr-2 inline-flex items-center gap-1 text-sm font-semibold text-emerald-700 hover:underline">
                          <Check className="h-3.5 w-3.5" /> Approve
                        </button>
                        <button onClick={() => handleReject(notice)} title="Reject" className="mr-3 inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:underline">
                          <X className="h-3.5 w-3.5" /> Reject
                        </button>
                      </>
                    )}
                    <button onClick={() => openEdit(notice)} className="mr-3 text-sm font-semibold text-blue-600 hover:underline">Edit</button>
                    <button onClick={() => handleDelete(notice)} className="text-sm font-semibold text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
              {(data?.content ?? []).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No notices yet. Create one to get started.</td></tr>
              )}
            </tbody>
          </table>
          <div className="border-t px-5 py-3 flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {data ? `${data.totalElements.toLocaleString()} total — Page ${data.number + 1} of ${Math.max(1, data.totalPages)}` : ""}
            </span>
            <div className="flex gap-2">
              <select value={size} onChange={(e) => setSize(Number(e.target.value))} className="rounded-lg border border-slate-200 px-2 py-1.5">
                {[10, 25, 50].map((o) => <option key={o} value={o}>{o} rows</option>)}
              </select>
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={!data || data.first}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Prev</button>
              <button onClick={() => setPage(page + 1)} disabled={!data || data.last}
                className="rounded-lg border px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
